import { HorizontalAlignment, VerticalAlignment } from "../../constants/Alignment";

/**
 * This defines the theme properties for the basic Component and its subclasses.
 *
 * Since this is a common theme constant, nothing here should reference colors directly and should
 * only refer to palette members.
 *
 * Random note: elements here can do any of the following... (maybe need to better distinguish which are which)
 *   - Be applied to a Component (so we are able to set any number of properties that are valid for Component and/or the subclass that is using the theme)
 *   - Be applied to a specific Component property (so we provide just the property value for a single property of Component and/or the subclass that is using the theme)
 *   - Be applied to an HTMLElement's style (so we are able to set any number of CSSStyleDeclaration members)
 *   - Be applied to a specific HTMLElement's style property (so we provide the value for a single member of CSSStyleDeclaration)
 */
export const ThemeCommonComponents = {
    button: {
        rippleColor: "primary.lighter",
        toggleBackgroundColor: "subtle.lighter"
    },
    checkbox: {
        image: {
            minHeight: 28,
            minWidth: 28,
            padding: 4
        },
        styleHoverCircle: { // styleHoverCircle is applied to an HTMLElement's style, not a Component (so Component props are not valid)
            backgroundColor: "component.palette.checkbox.hover.backgroundColor",
            borderRadius: "50%"
        }
    },
    list: {
        selection: {
            base: "primary.light",
            reverse: "primary.reverse"
        }
    },
    label: {
        variants: {
            caption: {
                allowSelect: false,
                color: "component.palette.label.variants.caption.color",
                fontSize: "small",
                padding: 0,
                readMoreType: "none" // Cannot import ReadMoreType here                    
            },
            pageTitle: {
                color: "subtle.darker",
                fillHeight: false,
                fontBold: true,
                fontSize: "xlarge",
                margin: 0,
                padding: 0,
                paddingLeft: 12
            },
            sectionHeading: {
                color: "McLeodPrimary",
                fontBold: true,
                fontSize: "xlarge"
            }
        }
    },
    numberEditor: {
        buttons: {
            color: "primary.reverse",
            backgroundColor: "primary"
        }
    },
    slideoutDecorator: {
        closeButton: {
            imageName: "x", // Can't use ImageName.x because it's in images
            variant: "round", //can't use ButtonVariant.ROUND because it's in components...we could move it...
            imageHeight: 28,
            imageWidth: 28
        },
        contentPanel: {
            fillRow: true,
            fillHeight: true,
            padding: 0
        },
        headerPanel: {
            fillRow: true,
            backgroundColor: "primary.darker",
            paddingLeft: 10,
            color: "primary.reverse"
        },
        outerPanel: {
            backgroundColor: "backgroundSubtle",
            fillRow: true,
            padding: 0,
            borderShadow: true
        },
        titleLabel: {
            imageColor: "primary.light",
            imageMarginRight: 4,
            align: HorizontalAlignment.LEFT,
            fontBold: true,
            fontSize: "large",
        }
    },
    textbox: {
        button: {
            color: "component.palette.textbox.button.color"
        },
        disabled: "component.palette.textbox.disabled",
        enabled: "component.palette.textbox.enabled",
    },
    editRowDecorator: {
        deleteButton: {
            imageName: "delete", // Can't use ImageName.delete because it's in images
            color: "primary.light",
            marginRight: 16,
            variant: "round", //can't use ButtonVariant.ROUND because it's in components...we could move it...
            imageWidth: 28,
            imageHeight: 28
        },
        multiButton: {
            backgroundColor: "primary"
        },
        outerPanel: {
            paddingBottom: 8,
        },
        primaryButton: {
            width: 100
        }
    },
    snackbar: {
        backgroundColor: "component.palette.snackbar.backgroundColor",
        color: "component.palette.snackbar.color",
        paddingLeft: 20,
        paddingRight: 20,
        paddingTop: 12,
        paddingBottom: 12
    },
    switch: {
        captions: {
            fontSize: "small",
            padding: 0,
            allowSelect: false,
            margin: "auto"
        }
    },
    splitter: {
        divider: {
            backgroundColor: "primary.darker",
            color: "primary.reverse"
        },
        buttonContainer: {
            borderRadius: 4,
            padding: 2,
            color: "primary.reverse",
            backgroundColor: "primary.darker"
        },
        buttons: {
            imageName: "chevron", // Can't use ImageName.chevron because it's in images
            imageWidth: 16,
            imageHeight: 16,
            padding: 1,
            margin: 0
        }
    },
    starRating: {
        all: {
            stroke: "warning"
        },
        filledColor: "warning.light",
        unfilledColor: "transparent"
    },
    table: {
        editRow: {
            textbox: {
                backgroundColor: "component.palette.editRow.textbox.backgroundColor",
            }
        },
        headingRow: {
            backgroundColor: "component.palette.table.headingRow.backgroundColor",
            color: "component.palette.table.headingRow.color",
        },
        selectionBackground: "component.palette.table.selection.backgroundColor",
        hoverBackground: "component.palette.table.hover.backgroundColor",
        sortSelectorFieldLabel: {
            height: 24,
            paddingRight: 0,
            marginRight: 0,
            color: "McLeodSecondary"
        },
        sortSelectorImageLabel: {
            height: 24,
            padding: 0,
            marginLeft: 8,
            marginRight: 0,
            imageHeight: 17,
            imageWidth: 17,
            color: "McLeodSecondary"
        },
        sortSelectorOrderLabel: {
            padding: 0,
            color: "McLeodLinkSpecial",
            fontSize: "xxsmall"
        },
        tooltip: {
            backgroundColor: "#ededed"
        }
    },
    tableRow: {
        slideoutButton: {
            imageName: "pencil", // Can't use ImageName.pencil because it's in images
            color: "primary",
            variant: "round", //can't use ButtonVariant.ROUND because it's in components...we could move it...
            imageHeight: 18,
            imageWidth: 18,
            focusable: false,
        },
        deleteButton: {
            imageName: "circleX", // Can't use ImageName.circleX because it's in images
            variant: "round", //can't use ButtonVariant.ROUND because it's in components...we could move it...
            focusable: false,
            color: "error"
        },
        dragHandler: {
            name: "hamburger",
            color: "primary",
            height: 18,
            width: 18,
            cursor: "grab" //can't use Cursor.GRAB because it's in components...we could move it...
        },
        quickAddButton: {
            imageProps: {
                name: "circlePlusFilled",
                stroke: "primary.reverse",
                fill: "primary"
            },
            variant: "round", //can't use ButtonVariant.ROUND because it's in components...we could move it...
            focusable: false,
            color: "primary",
        },
        expanderButtonLeft: {
            caption: "+",
            width: 18
        },
        expanderButtonRight: {
            imageName: "chevron", // Can't use ImageName.chevron because it's in images
            imageWidth: 20,
            imageHeight: 20,
            imageRotation: 90,
            color: "strokePrimary",
            margin: 0,
            padding: 0
        }
    },
    tabset: {
        headingAccordion: {
            align: HorizontalAlignment.LEFT,
            borderColor: "subtle.light",
            borderWidth: 1,
            borderRadius: 4,
            color: "subtle.darker",
            fillRow: true,
            paddingLeft: 18,
            paddingRight: 18,
            marginRight: 0,
            marginBottom: 8,
        },
        headingCommon: {
            color: "McLeodPrimary",
            cursor: "pointer", //can't use Cursor.GRAB because it's in components
            verticalAlign: VerticalAlignment.CENTER,
            wrap: false
        },
        headingCommonLabel: {
            allowSelect: false,
            collapseVerticallyWhenEmpty: true,
            paddingLeft: 0,
            paddingRight: 0,
            wrap: false
        },
        headingOutlinedSelected: {
            backgroundColor: "defaultBackground",
            color: "primary",
            fontBold: true
        },
        headingOutlinedUnselected: {
            backgroundColor: "backgroundSubtle",
            color: "subtle.darker",
            fontBold: false
        },
        headingScroll: {
            borderRadius: 0,
            borderWidth: 0,
            fillRow: false,
            paddingLeft: 12,
            paddingRight: 12,
            marginBottom: 8,
            marginLeft: 0,
            marginRight: 24,
            marginTop: 8
        },
        headingScrollSelected: {
            color: "primary",
            fontBold: true
        },
        headingScrollUnselected: {
            color: "subtle.darker",
            fontBold: false
        },
        headingUnderlined: {
            borderRadius: 0,
            fillRow: false,
            marginRight: 0
        },
        headingUnderlinedSelectedHorizontal: {
            color: "primary",
            fontBold: true
        },
        headingUnderlinedUnselectedHorizontal: {
            color: "default.light",
            fontBold: false
        },
        headingUnderlinedSelectedVertical: {
            backgroundColor: null,
            color: "primary",
            fontBold: true
        },
        headingUnderlinedUnselectedVertical: {
            backgroundColor: "backgroundSubtle",
            color: "default.light",
            fontBold: false
        }
    },
    toast: {
        backgroundColor: "primary",
        color: "primary.reverse",
        paddingLeft: 20,
        paddingRight: 20,
        paddingTop: 12,
        paddingBottom: 12,
        defaultLabel: {
            margin: 0,
            padding: 0,
            fontSize: "large"
        },
        closeImage: {
            name: "x",
            height: 24,
            width: 24,
            padding: 0,
            margin: 0,
            marginLeft: 6,
            verticalAlign: VerticalAlignment.CENTER
        },
        success: {
            backgroundColor: "primary",
            color: "primary.reverse",
            paddingLeft: 20,
            paddingRight: 20,
            paddingTop: 12,
            paddingBottom: 12,
            image: {
                height: 40,
                width: 40,
                color: "primary.reverse",
                marginRight: 8
            },
            titleLabel: {
                margin: 0,
                padding: 0,
                fontSize: "xxlarge"
            },
            messageLabel: {
                fontSize: "medium",
                margin: 0,
                marginTop: 6,
                padding: 0
            }
        }
    },
    clock: {
        backgroundColor: "component.palette.clock.backgroundColor",
        hand: {
            backgroundColor: "component.palette.clock.hand.backgroundColor",
        },
        selection: {
            backgroundColor: "component.palette.clock.selection.backgroundColor",
        },
        numbers: {
            color: "component.palette.clock.numbers.color",
        }
    },
    radioGroup: {
        border: {
            borderColor: "transparent",
            borderWidth: 1
        },
        borderErrorColor: "error"
    },
    radioBoxOption: {
        base: {
            paddingLeft: 8,
            paddingRight: 8
        },
        border: {
            borderRadius: 4,
            borderWidth: 1
        },
        disabled: {
            color: "subtle.light"
        },
        enabled: {
            color: ""
        },
        // styleHoverBorder is applied to an HTMLElement's style, not a Component (so Component props are not valid)
        styleHoverBorder: {
            borderColor: "#137cdd !important",
            boxShadow: "inset 0 0 0 1px #137cdd"
        },
        selectedBackgroundColor: "primary",
        selectedTextColor: "primary.reverse",
        unselectedBackgroundColor: "transparent",
        unselectedBorderColor: "subtle.darker"
    },
    radioButton: {
        displayValue: {
            paddingLeft: 0,
            marginLeft: 3
        },
        disabled: {
            color: "subtle.light"
        },
        enabled: {
            color: ""
        },
        image: {
            color: "component.palette.radioButton.color",
            name: "radioOff",
            marginRight: 0
        },
        size: 28,
        // styleHoverCircle is applied to an HTMLElement's style, not a Component (so Component props are not valid)
        styleHoverCircle: {
            backgroundColor: "component.palette.radioButton.hover.backgroundColor",
            borderRadius: "50%"
        }
    },
    radioOption: {
        base: {
            paddingLeft: 0
        },
        displayValue: {
            color: "default"
        }
    }
}
